import { useEffect, useState } from 'react';

/** Minimal History API router: path state for the whole app, no dependency. */
const ROUTE_CHANGE_EVENT = 'wow_calcs:routechange';

/**
 * Marks history entries the app pushed itself. Browsers keep entry state across reloads and
 * back/forward, so an entry without it is one the visitor landed on from outside (a shared link).
 */
const IN_APP_STATE = { inApp: true };

export function navigate(path: string, options: { replace?: boolean } = {}): void {
    if (path === window.location.pathname) {
        return;
    }
    if (options.replace) {
        window.history.replaceState(IN_APP_STATE, '', path);
    } else {
        window.history.pushState(IN_APP_STATE, '', path);
    }
    window.dispatchEvent(new Event(ROUTE_CHANGE_EVENT));
}

/** Re-reads `read()` on every history change. */
function useHistoryValue<T>(read: () => T): T {
    const [value, setValue] = useState(read);

    useEffect(() => {
        const update = () => setValue(read);
        window.addEventListener('popstate', update);
        window.addEventListener(ROUTE_CHANGE_EVENT, update);
        return () => {
            window.removeEventListener('popstate', update);
            window.removeEventListener(ROUTE_CHANGE_EVENT, update);
        };
    }, []);

    return value;
}

export function usePathname(): string {
    return useHistoryValue(() => window.location.pathname);
}

/** True on a report URL opened directly (shared link) rather than reached through the app. */
export function useSharedReportView(): boolean {
    return useHistoryValue(() => window.location.pathname.includes('/report/') && window.history.state?.inApp !== true);
}
